import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LucideArrowDown, LucideArrowUp } from '@lucide/angular';
import { ThemePreference, ThemeService } from '../../core/theme/theme.service';
import { MeService } from './me.service';
import { DayStatusCycleKind, MeResponse, ScheduleGroupUpdateInput, UpdateSettingsRequest } from './me.model';
import { describeGenericApiError, extractApiErrorMessage } from '../../core/http/api-error.util';
import { ScheduleType } from '../activities/activity-type.model';
import { SCHEDULE_GROUP_DEFAULT_LABELS, SCHEDULE_GROUP_ORDER } from '../today/schedule-group.util';
import { resolveCycleDisplayLabel as resolveDayStatusCycleDisplayLabel } from '../today/day-status-cycles.util';

/**
 * SettingsComponent allows users to view and update their personal settings such as timezone,
 * day window start time, and default slot duration. It fetches the current settings on initialisation
 * and provides a form for users to make changes. The component handles loading states, error messages,
 * and displays success feedback upon saving changes.
 *
 * @author sadatmalik
 */
@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideArrowUp, LucideArrowDown],
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit {
  private readonly meService = inject(MeService);
  private readonly themeService = inject(ThemeService);

  me = signal<MeResponse | null>(null);
  loading = signal(false);
  error = signal<string | null>(null);
  saveSuccess = signal(false);
  submitting = signal(false);
  reorderSubmitting = signal(false);

  timezone = '';
  themePreference: ThemePreference = 'SYSTEM';
  dayWindowStartLocalTime = '';
  dayWindowEndLocalTime = '';
  defaultSlotMinutes: number | null = null;
  focusDailyTarget: number | null = null;
  priorityDailyTarget: number | null = null;
  scheduleGroupCount = 2;
  scheduleGroups: Array<{ ordinal: ScheduleType; label: string; budgetMinutes: number | null; position: number; cycle: 'WORK' | 'LIFE' }> =
    this.defaultScheduleGroups();
  workCycleLabel = '';
  lifeCycleLabel = '';

  readonly minSlotMinutes = 1;
  readonly maxSlotMinutes = 480;
  readonly minFocusDailyTarget = 1;
  readonly maxFocusDailyTarget = 20;
  readonly cycleLabelMaxLength = 24;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.meService.getMe().subscribe({
      next: (data) => {
        this.me.set(data);
        this.timezone = data.settings?.timezone ?? 'UTC';
        this.themePreference = data.settings?.themePreference ?? 'SYSTEM';
        this.dayWindowStartLocalTime = this.toTimeInputValue(data.settings?.dayWindowStartLocalTime ?? '00:00');
        this.dayWindowEndLocalTime = this.toTimeInputValue(data.settings?.dayWindowEndLocalTime ?? '12:00');
        this.defaultSlotMinutes = data.settings?.defaultSlotMinutes ?? 40;
        this.focusDailyTarget = data.settings?.focusDailyTarget ?? 3;
        this.priorityDailyTarget = data.settings?.priorityDailyTarget ?? 3;
        this.scheduleGroupCount = this.normalizeScheduleGroupCount(data.settings?.scheduleGroupCount ?? 2);
        this.scheduleGroups = this.hydrateScheduleGroupDrafts(data);
        this.workCycleLabel = data.settings?.workCycleLabel ?? '';
        this.lifeCycleLabel = data.settings?.lifeCycleLabel ?? '';
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(
          extractApiErrorMessage(err) ??
          describeGenericApiError(err, 'Failed to load settings')
        );
        this.loading.set(false);
      }
    });
  }

  save(): void {
    const tz = this.timezone?.trim() || undefined;
    const dayStart = this.dayWindowStartLocalTime?.trim() || undefined;
    const dayEnd = this.dayWindowEndLocalTime?.trim() || undefined;
    const slot = this.defaultSlotMinutes != null ? this.defaultSlotMinutes : undefined;
    const focusDailyTarget = this.focusDailyTarget != null ? this.focusDailyTarget : undefined;
    const priorityDailyTarget = this.priorityDailyTarget != null ? this.priorityDailyTarget : undefined;
    const request: UpdateSettingsRequest = {};
    if (tz) request.timezone = tz;
    const currentTheme = this.me()?.settings?.themePreference ?? 'SYSTEM';
    if (this.themePreference !== currentTheme) {
      request.themePreference = this.themePreference;
    }
    if (dayStart) request.dayWindowStartLocalTime = this.fromTimeInputValue(dayStart);
    if (dayEnd) request.dayWindowEndLocalTime = this.fromTimeInputValue(dayEnd);
    if (slot != null && slot >= this.minSlotMinutes && slot <= this.maxSlotMinutes) {
      request.defaultSlotMinutes = slot;
    }
    if (focusDailyTarget != null
      && focusDailyTarget >= this.minFocusDailyTarget
      && focusDailyTarget <= this.maxFocusDailyTarget) {
      request.focusDailyTarget = focusDailyTarget;
    }
    if (priorityDailyTarget != null
      && priorityDailyTarget >= this.minFocusDailyTarget
      && priorityDailyTarget <= this.maxFocusDailyTarget) {
      request.priorityDailyTarget = priorityDailyTarget;
    }
    const currentSettings = this.me()?.settings;
    const normalizedWorkCycleLabel = this.normalizeCycleLabel(this.workCycleLabel);
    const normalizedLifeCycleLabel = this.normalizeCycleLabel(this.lifeCycleLabel);
    const existingWorkCycleLabel = this.normalizeCycleLabel(currentSettings?.workCycleLabel ?? '');
    const existingLifeCycleLabel = this.normalizeCycleLabel(currentSettings?.lifeCycleLabel ?? '');
    if (normalizedWorkCycleLabel !== existingWorkCycleLabel) {
      request.workCycleLabel = normalizedWorkCycleLabel ?? '';
    }
    if (normalizedLifeCycleLabel !== existingLifeCycleLabel) {
      request.lifeCycleLabel = normalizedLifeCycleLabel ?? '';
    }
    if (currentSettings && currentSettings.scheduleGroupCount !== this.scheduleGroupCount) {
      request.scheduleGroupCount = this.scheduleGroupCount;
    }
    const groupUpdates = this.buildScheduleGroupUpdates();
    if (groupUpdates.length > 0) {
      request.scheduleGroups = groupUpdates;
    }
    if (Object.keys(request).length === 0) {
      return;
    }

    this.submitting.set(true);
    this.error.set(null);
    this.saveSuccess.set(false);
    this.meService.updateSettings(request).subscribe({
      next: (data) => {
        this.me.set(data);
        this.timezone = data.settings?.timezone ?? this.timezone;
        this.themePreference = data.settings?.themePreference ?? this.themePreference;
        this.dayWindowStartLocalTime = this.toTimeInputValue(data.settings?.dayWindowStartLocalTime ?? '00:00');
        this.dayWindowEndLocalTime = this.toTimeInputValue(data.settings?.dayWindowEndLocalTime ?? '12:00');
        this.defaultSlotMinutes = data.settings?.defaultSlotMinutes ?? this.defaultSlotMinutes;
        this.focusDailyTarget = data.settings?.focusDailyTarget ?? this.focusDailyTarget;
        this.priorityDailyTarget = data.settings?.priorityDailyTarget ?? this.priorityDailyTarget;
        this.scheduleGroupCount = this.normalizeScheduleGroupCount(data.settings?.scheduleGroupCount ?? this.scheduleGroupCount);
        this.scheduleGroups = this.hydrateScheduleGroupDrafts(data);
        this.workCycleLabel = data.settings?.workCycleLabel ?? '';
        this.lifeCycleLabel = data.settings?.lifeCycleLabel ?? '';
        this.saveSuccess.set(true);
        this.submitting.set(false);
      },
      error: (err) => {
        this.error.set(
          extractApiErrorMessage(err) ??
          describeGenericApiError(err, 'Failed to save settings')
        );
        this.submitting.set(false);
      }
    });
  }

  onThemeChange(): void {
    this.themeService.apply(this.themePreference);
  }

  /** Convert API time (HH:mm or HH:mm:ss) to input[type="time"] value (HH:mm) */
  private toTimeInputValue(apiTime: string): string {
    if (!apiTime) return '00:00';
    const part = apiTime.slice(0, 5);
    return part.length === 5 ? part : '00:00';
  }

  /** Keep HH:mm for API (backend accepts HH:mm) */
  private fromTimeInputValue(inputValue: string): string {
    return inputValue?.slice(0, 5) || '00:00';
  }

  resolveGroupLabel(ordinal: ScheduleType): string {
    return SCHEDULE_GROUP_DEFAULT_LABELS[ordinal];
  }

  resolveCycleDisplayLabel(cycle: DayStatusCycleKind): string {
    return resolveDayStatusCycleDisplayLabel(cycle, this.workCycleLabel, this.lifeCycleLabel);
  }

  trackScheduleGroup(_index: number, group: { ordinal: ScheduleType }): string {
    return group.ordinal;
  }

  isGroupEnabled(ordinal: ScheduleType): boolean {
    return SCHEDULE_GROUP_ORDER.indexOf(ordinal) < this.scheduleGroupCount;
  }

  canReorderGroup(ordinal: ScheduleType, direction: 'up' | 'down'): boolean {
    if (this.reorderSubmitting()) {
      return false;
    }
    const enabled = this.scheduleGroups.filter((group) => this.isGroupEnabled(group.ordinal));
    const index = enabled.findIndex((group) => group.ordinal === ordinal);
    if (index < 0) {
      return false;
    }
    return direction === 'up'
      ? index > 0
      : index < enabled.length - 1;
  }

  reorderGroup(ordinal: ScheduleType, direction: 'up' | 'down'): void {
    if (!this.canReorderGroup(ordinal, direction)) {
      return;
    }
    const previousDrafts = this.scheduleGroups;
    const updates = this.buildReorderUpdates(ordinal, direction);
    if (updates.length === 0) {
      return;
    }

    this.scheduleGroups = this.sortScheduleGroupDrafts(
      previousDrafts.map((group) => {
        const update = updates.find((candidate) => candidate.ordinal === group.ordinal);
        if (!update || update.position == null) {
          return group;
        }
        return { ...group, position: update.position };
      })
    );
    this.reorderSubmitting.set(true);
    this.error.set(null);
    this.saveSuccess.set(false);
    this.meService.updateSettings({ scheduleGroups: updates }).subscribe({
      next: (data) => {
        this.me.set(data);
        this.scheduleGroupCount = this.normalizeScheduleGroupCount(data.settings?.scheduleGroupCount ?? this.scheduleGroupCount);
        this.scheduleGroups = this.hydrateScheduleGroupDrafts(data);
        this.reorderSubmitting.set(false);
        this.saveSuccess.set(true);
      },
      error: (err) => {
        this.scheduleGroups = previousDrafts;
        this.error.set(
          extractApiErrorMessage(err) ??
          describeGenericApiError(err, 'Failed to reorder groupings')
        );
        this.reorderSubmitting.set(false);
      }
    });
  }

  private buildScheduleGroupUpdates(): ScheduleGroupUpdateInput[] {
    const currentByOrdinal = new Map((this.me()?.settings?.scheduleGroups ?? []).map((group) => [group.ordinal, group]));
    return this.scheduleGroups.flatMap((groupDraft) => {
      const existing = currentByOrdinal.get(groupDraft.ordinal);
      const normalizedLabel = groupDraft.label.trim() || null;
      const normalizedBudgetMinutes = this.normalizeBudgetMinutes(groupDraft.budgetMinutes);
      const normalizedPosition = this.normalizeScheduleGroupPosition(groupDraft.position, SCHEDULE_GROUP_ORDER.indexOf(groupDraft.ordinal));
      const existingLabel = existing?.label ?? null;
      const existingBudgetMinutes = existing?.budgetMinutes ?? null;
      const existingPosition = this.normalizeScheduleGroupPosition(
        existing?.position,
        SCHEDULE_GROUP_ORDER.indexOf(groupDraft.ordinal)
      );
      const existingCycle = existing?.cycle === 'LIFE' ? 'LIFE' : 'WORK';
      if (
        existingLabel === normalizedLabel
        && existingBudgetMinutes === normalizedBudgetMinutes
        && existingPosition === normalizedPosition
        && existingCycle === groupDraft.cycle
      ) {
        return [];
      }
      return [{
        ordinal: groupDraft.ordinal,
        label: normalizedLabel,
        budgetMinutes: normalizedBudgetMinutes,
        position: normalizedPosition,
        cycle: groupDraft.cycle
      }];
    });
  }

  private hydrateScheduleGroupDrafts(
    data: MeResponse
  ): Array<{ ordinal: ScheduleType; label: string; budgetMinutes: number | null; position: number; cycle: 'WORK' | 'LIFE' }> {
    const byOrdinal = new Map((data.settings?.scheduleGroups ?? []).map((group) => [group.ordinal, group]));
    const drafts = SCHEDULE_GROUP_ORDER.map((ordinal, index) => {
      const existing = byOrdinal.get(ordinal);
      return {
        ordinal,
        label: existing?.label ?? '',
        budgetMinutes: this.normalizeBudgetMinutes(existing?.budgetMinutes ?? null),
        position: this.normalizeScheduleGroupPosition(existing?.position, index),
        cycle: existing?.cycle === 'LIFE' ? 'LIFE' as const : 'WORK' as const
      };
    });
    return this.sortScheduleGroupDrafts(drafts);
  }

  private defaultScheduleGroups(): Array<{ ordinal: ScheduleType; label: string; budgetMinutes: number | null; position: number; cycle: 'WORK' | 'LIFE' }> {
    return SCHEDULE_GROUP_ORDER.map((ordinal, index) => ({
      ordinal,
      label: '',
      budgetMinutes: null,
      position: index,
      cycle: 'WORK'
    }));
  }

  private normalizeBudgetMinutes(value: number | null | undefined): number | null {
    if (value == null) {
      return null;
    }
    if (!Number.isFinite(value)) {
      return null;
    }
    return Math.max(0, Math.floor(value));
  }

  private normalizeScheduleGroupPosition(value: number | null | undefined, fallback: number): number {
    if (!Number.isFinite(value)) {
      return fallback;
    }
    return Math.max(0, Math.min(4, Math.floor(Number(value))));
  }

  private sortScheduleGroupDrafts(
    groups: Array<{ ordinal: ScheduleType; label: string; budgetMinutes: number | null; position: number; cycle: 'WORK' | 'LIFE' }>
  ): Array<{ ordinal: ScheduleType; label: string; budgetMinutes: number | null; position: number; cycle: 'WORK' | 'LIFE' }> {
    const fallbackOrder = new Map(SCHEDULE_GROUP_ORDER.map((ordinal, index) => [ordinal, index]));
    const enabled = groups
      .filter((group) => this.isGroupEnabled(group.ordinal))
      .slice()
      .sort((left, right) => {
        if (left.position !== right.position) {
          return left.position - right.position;
        }
        return (fallbackOrder.get(left.ordinal) ?? 0) - (fallbackOrder.get(right.ordinal) ?? 0);
      });
    const disabled = groups
      .filter((group) => !this.isGroupEnabled(group.ordinal))
      .slice()
      .sort((left, right) => {
        if (left.position !== right.position) {
          return left.position - right.position;
        }
        return (fallbackOrder.get(left.ordinal) ?? 0) - (fallbackOrder.get(right.ordinal) ?? 0);
      });
    return [...enabled, ...disabled];
  }

  private buildReorderUpdates(sourceOrdinal: ScheduleType, direction: 'up' | 'down'): ScheduleGroupUpdateInput[] {
    const enabled = this.scheduleGroups.filter((group) => this.isGroupEnabled(group.ordinal));
    const sourceIndex = enabled.findIndex((group) => group.ordinal === sourceOrdinal);
    if (sourceIndex < 0) {
      return [];
    }
    const targetIndex = direction === 'up' ? sourceIndex - 1 : sourceIndex + 1;
    if (targetIndex < 0 || targetIndex >= enabled.length) {
      return [];
    }
    const reorderedEnabled = [...enabled];
    const [moved] = reorderedEnabled.splice(sourceIndex, 1);
    reorderedEnabled.splice(targetIndex, 0, moved);

    const nextPositionsByOrdinal = new Map<ScheduleType, number>();
    reorderedEnabled.forEach((group, index) => nextPositionsByOrdinal.set(group.ordinal, index));
    const disabledOrdinals = SCHEDULE_GROUP_ORDER.filter((ordinal) => !nextPositionsByOrdinal.has(ordinal));
    disabledOrdinals.forEach((ordinal, index) => {
      nextPositionsByOrdinal.set(ordinal, reorderedEnabled.length + index);
    });

    const currentByOrdinal = new Map(this.scheduleGroups.map((group) => [group.ordinal, group]));
    return SCHEDULE_GROUP_ORDER.map((ordinal) => ({
      ordinal,
      budgetMinutes: currentByOrdinal.get(ordinal)?.budgetMinutes ?? null,
      position: nextPositionsByOrdinal.get(ordinal) ?? 0
    }));
  }

  private normalizeScheduleGroupCount(value: number): number {
    if (!Number.isFinite(value)) {
      return 2;
    }
    return Math.max(1, Math.min(5, Math.floor(value)));
  }

  private normalizeCycleLabel(value: string | null | undefined): string | null {
    const trimmed = value?.trim() ?? '';
    return trimmed.length > 0 ? trimmed : null;
  }
}
